import type { QueryClient } from "@tanstack/react-query";

import type { PageDescriptor } from "@pm2/api";
import {
  AccessCodes,
  getPageContent,
  getPageMetadata,
  getTeacherGuide,
} from "@pm2/api";
import type {
  AccordionComponentNode,
  CardsComponentNode,
} from "@pm2/content-engine";
import {
  cardsComponentInTeacherSectionParsers,
  makeAccordionComponentParsers,
} from "@pm2/content-engine";
import { getAllRevisionsQuery } from "@pm2/course-revisions";
import type { PM2SitemapStore } from "@pm2/sitemap-utils";
import { extractResourceGroups, getResourcesForPage } from "@pm2/sitemap-utils";
import { type PM2ConfigStore } from "@pm2/store";
import { BottomNavigation } from "@pm2/ui-layout";
import type { UserIdentity } from "@provider/user-identity";
import { xml2ast } from "@utils/ast";
import { prefetchQueryIfNeeded } from "@utils/common";

export async function renderCourseFrontpage(
  user: UserIdentity | null | undefined,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  queryClient: QueryClient,
  pageDescriptor: PageDescriptor,
): Promise<React.JSX.Element> {
  const configState = configStore.getState();
  const teachersGuidePage = pageDescriptor.children?.find(
    (it) => it.type === "TeachersGuidePage",
  );
  const isAuthorized = teachersGuidePage?.accessCode === AccessCodes.ALLOWED;

  const [
    { CourseFrontPage, extractCourseMetadata },
    resources,
    metadata,
    teacherGuide,
    teacherGuidePageContent,
  ] = await Promise.all([
    import("@pm2/course-frontpage"),
    getResourcesForPage(configStore, sitemapStore, pageDescriptor),

    getPageMetadata(configState.config.id, pageDescriptor.id, {
      previewMode: configState.previewMode,
    }),
    getTeacherGuide(configState.config.id, pageDescriptor.id, {
      previewMode: configState.previewMode,
    }),
    teachersGuidePage && isAuthorized
      ? getPageContent(configState.config.id, teachersGuidePage.id, {
          previewMode: configState.previewMode,
        })
      : undefined,

    prefetchQueryIfNeeded(
      queryClient,
      getAllRevisionsQuery.queryConfig({ user, courseId: pageDescriptor?.id }),
    ),
  ]);

  const teachersGuidePageAccordions = xml2ast(
    teacherGuidePageContent,
    makeAccordionComponentParsers({ disableAutoCollapse: true }),
    "text/xml",
    { parseText: false, warnOnUnknownNode: false },
  ) as AccordionComponentNode[];

  const teachersCardsNode = xml2ast(
    teacherGuidePageContent,
    cardsComponentInTeacherSectionParsers,
    "text/xml",
    {
      parseText: false,
      warnOnUnknownNode: false,
      context: { titleAlignment: "left" },
    },
  )[0] as CardsComponentNode | undefined;

  return (
    <>
      <CourseFrontPage
        courseDescriptor={pageDescriptor}
        resources={extractResourceGroups(resources)}
        metadata={extractCourseMetadata(metadata)}
        teacherGuide={teacherGuide}
        teachersGuidePageAccordions={teachersGuidePageAccordions}
        teachersCardsNode={teachersCardsNode}
      />

      <BottomNavigation pageDescriptor={pageDescriptor} />
    </>
  );
}
